import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

type TopbarIslandOperationBase = {
  description?: string;
  id: string;
  title: string;
};

export type TopbarIslandOperationState =
  | (TopbarIslandOperationBase & {
      cancelLabel?: string;
      confirmLabel: string;
      icon?: string;
      onCancel?: () => void;
      onConfirm: () => void | Promise<void>;
      phase: "prompt";
    })
  | (TopbarIslandOperationBase & {
      phase: "loading";
    })
  | (TopbarIslandOperationBase & {
      icon?: string;
      phase: "result";
      tone: "error" | "info" | "success" | "warning";
    });

export type TopbarIslandViewStateMap = {
  operation: TopbarIslandOperationState;
  notification: {
    unreadCount?: number;
  };
  task: {
    id: string;
    label: string;
    progress?: number;
  };
};

export type TopbarIslandView = keyof TopbarIslandViewStateMap;

export type TopbarIslandState = {
  [TView in TopbarIslandView]: {
    view: TView;
    data: TopbarIslandViewStateMap[TView];
  };
}[TopbarIslandView];

export type ShowTopbarIslandOptions = {
  /** Milliseconds before dismissal. Use `null` to keep the view open until explicitly dismissed or replaced. */
  duration?: number | null;
  /** `background` waits behind other views; `interrupt` restores a persistent view; `replace` discards it. */
  priority?: "background" | "interrupt" | "replace";
};

export type TopbarIslandController = {
  /** Without an identity, dismisses the visible view. Tasks and operations can also be dismissed by data.id while interrupted. */
  dismiss: (identity?: string) => void;
  show: <TView extends TopbarIslandView>(view: TView, state: TopbarIslandViewStateMap[TView], options?: ShowTopbarIslandOptions) => void;
};

type TopbarIslandContextValue = TopbarIslandController & {
  available: boolean;
  setHostAvailable: (available: boolean) => void;
  state: TopbarIslandState | null;
};

declare global {
  interface Window {
    __topbarIslandQa?: TopbarIslandController;
  }
}

type TopbarIslandEntry = {
  duration: number | null;
  identity: string;
  state: TopbarIslandState;
};

const DEFAULT_VISIBLE_DURATION = 5_000;
const TopbarIslandContext = createContext<TopbarIslandContextValue | null>(null);

function getStateIdentity(state: TopbarIslandState) {
  return state.view === "task" || state.view === "operation" ? state.data.id : state.view;
}

export function TopbarIslandProvider({ children }: { children: ReactNode }) {
  const [available, setHostAvailable] = useState(false);
  const [state, setState] = useState<TopbarIslandState | null>(null);
  const activeEntryRef = useRef<TopbarIslandEntry | null>(null);
  const suspendedEntryRef = useRef<TopbarIslandEntry | null>(null);
  const backgroundEntryRef = useRef<TopbarIslandEntry | null>(null);
  const dismissTimerRef = useRef<number | null>(null);
  const presentationVersionRef = useRef(0);

  const clearDismissTimer = useCallback(() => {
    if (dismissTimerRef.current !== null) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = null;
    }
  }, []);

  const present = useCallback(
    (entry: TopbarIslandEntry | null) => {
      clearDismissTimer();
      const presentationVersion = ++presentationVersionRef.current;
      activeEntryRef.current = entry;
      setState(entry?.state ?? null);

      if (entry?.duration !== null && entry?.duration !== undefined) {
        dismissTimerRef.current = window.setTimeout(() => {
          if (presentationVersionRef.current !== presentationVersion) {
            return;
          }

          dismissTimerRef.current = null;
          const suspendedEntry = suspendedEntryRef.current ?? backgroundEntryRef.current;
          suspendedEntryRef.current = null;
          activeEntryRef.current = suspendedEntry;
          setState(suspendedEntry?.state ?? null);
        }, entry.duration);
      }
    },
    [clearDismissTimer],
  );

  const dismiss = useCallback(
    (identity?: string) => {
      const activeEntry = activeEntryRef.current;

      if (identity && backgroundEntryRef.current?.identity === identity) {
        backgroundEntryRef.current = null;
      }

      if (identity && activeEntry?.identity !== identity) {
        if (suspendedEntryRef.current?.identity === identity) {
          suspendedEntryRef.current = null;
        }
        return;
      }

      const suspendedEntry = suspendedEntryRef.current ?? backgroundEntryRef.current;
      suspendedEntryRef.current = null;
      present(suspendedEntry);
    },
    [present],
  );

  const show = useCallback<TopbarIslandController["show"]>(
    (view, state, options) => {
      const nextState = { view, data: state } as TopbarIslandState;
      const identity = getStateIdentity(nextState);
      const existingEntry = [activeEntryRef.current, suspendedEntryRef.current].find((entry) => entry?.identity === identity);
      const duration = options?.duration === undefined ? (existingEntry?.duration ?? DEFAULT_VISIBLE_DURATION) : options.duration;
      const nextEntry: TopbarIslandEntry = {
        duration,
        identity,
        state: nextState,
      };

      if (options?.priority === "background") {
        backgroundEntryRef.current = nextEntry;
        if (!activeEntryRef.current || activeEntryRef.current.identity === identity) {
          present(nextEntry);
        }
        return;
      }

      if (options?.priority === "replace") {
        suspendedEntryRef.current = null;
      }

      const activeEntry = activeEntryRef.current;
      if (activeEntry?.identity === nextEntry.identity) {
        present(nextEntry);
        return;
      }

      const suspendedEntry = suspendedEntryRef.current;
      if (suspendedEntry?.identity === nextEntry.identity && duration === null) {
        // Keep an interrupted task out of the way while still accepting progress updates.
        suspendedEntryRef.current = nextEntry;
        return;
      }

      if (duration === null) {
        suspendedEntryRef.current = null;
      } else if (options?.priority !== "replace" && activeEntry?.duration === null && activeEntry.identity !== backgroundEntryRef.current?.identity) {
        suspendedEntryRef.current = activeEntry;
      }

      present(nextEntry);
    },
    [present],
  );

  useEffect(() => clearDismissTimer, [clearDismissTimer]);
  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    window.__topbarIslandQa = { dismiss, show };
    return () => {
      delete window.__topbarIslandQa;
    };
  }, [dismiss, show]);

  const value = useMemo(() => ({ available, dismiss, setHostAvailable, show, state }), [available, dismiss, show, state]);
  return <TopbarIslandContext.Provider value={value}>{children}</TopbarIslandContext.Provider>;
}

export function useTopbarIsland() {
  const value = useContext(TopbarIslandContext);
  if (!value) {
    throw new Error("useTopbarIsland must be used inside TopbarIslandProvider.");
  }
  return value;
}
